import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const RecipeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    fetchRecipe();
  }, [id]);

  const fetchRecipe = async () => {
    try {
      const response = await fetch(`/api/recipes/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Failed to fetch recipe');
      setRecipe(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`/api/recipes/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete recipe');
      }

      navigate('/recipes');
    } catch (err) {
      setError(err.message);
      setShowDeleteModal(false);
    }
  };

  if (loading) return <div style={styles.loading}>Loading recipe...</div>;
  if (error) return <div style={styles.error}>{error}</div>;
  if (!recipe) return <div style={styles.error}>Recipe not found</div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <Link to="/recipes" style={styles.backLink}>← Back to Recipes</Link>
        
        {recipe.canEdit && (
          <div style={styles.actions}>
            <Link to={`/recipes/${id}/edit`} style={styles.editBtn}>✏️ Edit</Link>
            <button onClick={() => setShowDeleteModal(true)} style={styles.deleteBtn}>🗑️ Delete</button>
          </div>
        )}
      </div>

      <div style={styles.card}>
        {/* Recipe Image - displayed above the title */}
        {recipe.imageUrl && (
          <div style={styles.imageContainer}>
            <img 
              src={recipe.imageUrl} 
              alt={recipe.name}
              style={styles.recipeImage}
            />
          </div>
        )}

        <h1 style={styles.title}>{recipe.name}</h1>
        <p style={styles.meta}>
          By {recipe.authorName || 'Unknown'} • 
          Created {new Date(recipe.createdAt).toLocaleDateString()}
        </p>
        
        {recipe.description && (
          <p style={styles.description}>{recipe.description}</p>
        )}

        {recipe.tags && recipe.tags.length > 0 && (
          <div style={styles.tagsSection}>
            {recipe.tags.map(tag => (
              <span key={tag.id} style={styles.tag}>{tag.name}</span>
            ))}
          </div>
        )}

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>🥕 Ingredients ({recipe.ingredients.length})</h2>
          <ul style={styles.list}>
            {recipe.ingredients.map(ing => (
              <li key={ing.id} style={styles.listItem}>
                <span style={styles.quantity}>{ing.quantity}</span>
                <span>{ing.name}</span>
              </li>
            ))}
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>📝 Steps ({recipe.steps.length})</h2>
          <ol style={styles.steps}>
            {recipe.steps.map(step => (
              <li key={step.id} style={styles.step}>
                {step.instruction}
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalIcon}>⚠️</div>
            <h3 style={styles.modalTitle}>Delete Recipe?</h3>
            <p style={styles.modalText}>
              Are you sure you want to delete "{recipe.name}"? This action cannot be undone.
            </p>
            <div style={styles.modalActions}>
              <button onClick={() => setShowDeleteModal(false)} style={styles.cancelBtn}>Cancel</button>
              <button onClick={handleDelete} style={styles.confirmDeleteBtn}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { maxWidth: '800px', margin: '0 auto', padding: '1rem 2rem' },
  loading: { textAlign: 'center', padding: '3rem', color: '#6b7280' },
  error: { backgroundColor: '#fee2e2', color: '#dc2626', padding: '1rem', borderRadius: '8px', margin: '2rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  backLink: { color: '#2563eb', textDecoration: 'none' },
  actions: { display: 'flex', gap: '0.5rem' },
  editBtn: { backgroundColor: '#fbbf24', color: '#1e3a5f', padding: '0.5rem 1rem', borderRadius: '6px', textDecoration: 'none', fontWeight: '600' },
  deleteBtn: { backgroundColor: '#ef4444', color: 'white', padding: '0.5rem 1rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '600' },
  card: { backgroundColor: 'white', borderRadius: '12px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', overflow: 'hidden' },
  
  // Image styles
  imageContainer: { 
    margin: '-2rem -2rem 1.5rem -2rem',  // Negative margin to extend to card edges
    backgroundColor: '#f3f4f6'
  },
  recipeImage: { 
    width: '100%', 
    maxHeight: '400px', 
    objectFit: 'cover',
    display: 'block'
  },
  
  title: { fontSize: '2rem', color: '#1e3a5f', margin: '0 0 0.5rem 0' },
  meta: { color: '#6b7280', fontSize: '0.9rem', marginBottom: '1rem' },
  description: { 
    color: '#374151', 
    fontSize: '1.1rem', 
    lineHeight: 1.6, 
    marginBottom: '1.5rem', 
    padding: '1rem', 
    backgroundColor: '#f9fafb', 
    borderRadius: '8px' 
  },
  section: { marginTop: '2rem' },
  sectionTitle: { fontSize: '1.3rem', color: '#1e3a5f', marginBottom: '1rem' },
  list: { listStyle: 'none', padding: 0, margin: 0 },
  listItem: { padding: '0.75rem', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: '1rem' },
  quantity: { fontWeight: '600', color: '#f97316', minWidth: '80px' },
  steps: { paddingLeft: '1.5rem' },
  step: { padding: '0.75rem 0', lineHeight: 1.6, color: '#374151' },
  
  // Modal styles
  modalOverlay: { 
    position: 'fixed', 
    top: 0, 
    left: 0, 
    right: 0, 
    bottom: 0, 
    backgroundColor: 'rgba(0,0,0,0.5)', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    zIndex: 1000 
  },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '400px', textAlign: 'center' },
  modalIcon: { fontSize: '3rem' },
  modalTitle: { fontSize: '1.3rem', color: '#1e3a5f', margin: '0.5rem 0' },
  modalText: { color: '#6b7280', marginBottom: '1.5rem' },
  modalActions: { display: 'flex', gap: '1rem', justifyContent: 'center' },
  cancelBtn: { backgroundColor: '#e5e7eb', color: '#374151', padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600' },
  confirmDeleteBtn: { backgroundColor: '#ef4444', color: 'white', padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600' },
  tagsSection: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.5rem' },
  tag: { backgroundColor: '#dbeafe', color: '#1e40af', padding: '0.35rem 0.85rem', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: '500' }
};

export default RecipeDetail;
